<ng-container *ngIf="game && season; else loading">

  <div class="header">
    <button mat-button (click)="back()">
      <mat-icon>arrow_back</mat-icon>
      Back
    </button>
    <h2 class="mat-h2">{{ game.name }}</h2>
    <span class="chip state-{{ game.state }}">{{ stateLabels[game.state] }}</span>
    <span class="grow"></span>
    <a mat-button [href]="game.dashboardUrl" target="_blank" rel="noopener">
      <mat-icon>open_in_new</mat-icon>
      StayLive dashboard
    </a>
  </div>

  <div class="subhead">
    {{ game.producerName }} · {{ game.date }} · {{ game.competition }}
    <span *ngIf="game.facts?.format"> · {{ game.facts.format }}</span>
    <span *ngIf="game.ctSportPlus" class="chip ct">Also on ČT Sport+</span>
    <!-- Commentary, ambient sound and the camera count are measured while the
         game is running. When that stretch is short, say so here rather than
         let the numbers look like they cover the whole broadcast. -->
    <span *ngIf="isAdmin && game.localFile" class="local"
          matTooltip="Path on the machine that analysed the game. Click to copy — browsers refuse to open file:// from a web page.">
      <a href="file://{{ game.localFile }}" (click)="copyLocalFile($event)">
        <mat-icon inline>movie</mat-icon>
        {{ copiedPath ? 'Path copied' : 'Recording on disk' }}
      </a>
    </span>
    <span *ngIf="shortPlaytime" class="chip warn"
          matTooltip="Commentary, ambient sound and the camera count are measured while the game is on screen.">
      Game on screen {{ game.facts.playShare | percent }} of the recording
    </span>
  </div>

  <mat-card *ngIf="game.measurementDrift" class="card drift">
    <mat-card-content>
      <b>The tool now measures this game differently than when it was approved.</b>
      <ul>
        <li *ngFor="let change of game.measurementDrift.changes">
          {{ criterionLabel(change.criterion) }}: {{ change.from }} → {{ change.to }}
        </li>
      </ul>
      <div class="muted">The approved figures stand; nothing was overwritten.</div>
    </mat-card-content>
  </mat-card>

  <!-- The recording, when this machine is the one that downloaded it. -->
  <app-recording-player *ngIf="isAdmin" #player [videoId]="game.videoId"
                        [localFile]="game.localFile">
  </app-recording-player>

  <!-- Payment -->
  <mat-card class="card">
    <mat-card-header>
      <mat-card-title>Payment</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <div class="warn-line" *ngIf="pending.length">
        {{ pending.length }} criteri{{ pending.length === 1 ? 'on' : 'a' }} still undecided —
        the amount below is not final.
      </div>
      <div class="bad-line" *ngIf="blocked">{{ blocked }}</div>
      <table class="reward">
        <tbody>
          <tr *ngFor="let line of reward.lines">
            <td>{{ line.label }}
              <div class="muted" *ngIf="line.detail">{{ line.detail }}</div>
            </td>
            <td class="amount">{{ line.amount | number }} {{ reward.currency }}</td>
          </tr>
          <tr class="total">
            <td>Total</td>
            <td class="amount">{{ reward.total | number }} {{ reward.currency }}</td>
          </tr>
        </tbody>
      </table>
      <div class="muted" *ngIf="game.reward?.computedAt">
        Approved at {{ game.reward.total | number }} {{ game.reward.currency }}
        by {{ game.approvedByName || game.approvedBy }}.
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Operated camera: the one criterion the tool cannot see -->
  <mat-card class="card">
    <mat-card-header>
      <mat-card-title>Operated camera</mat-card-title>
      <mat-card-subtitle>
        Nothing in the recording tells us this, so the producer declares it.
        A claim worth money is then an admin's to confirm; none is not, because
        it costs the producer the whole fee and nobody claims that falsely.
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <mat-radio-group class="camera-choice"
                       [ngModel]="declaration"
                       (ngModelChange)="declareCamera($event)"
                       [disabled]="!canAct || busy">
        <mat-radio-button [value]="0">None</mat-radio-button>
        <mat-radio-button [value]="0.5">An operated camera, but not on live play</mat-radio-button>
        <mat-radio-button [value]="1">Operated camera on live play</mat-radio-button>
      </mat-radio-group>
      <div class="muted" *ngIf="!game.declarations?.declaredAt">Not declared yet.</div>
      <div class="muted" *ngIf="game.declarations?.declaredAt">
        Declared by {{ game.declarations.declaredByName || game.declarations.declaredBy }}.
        <span *ngIf="declarationConfirmed">
          Confirmed by {{ game.declarations.confirmedByName || game.declarations.confirmedBy }}.
        </span>
        <!-- A claim worth money is somebody's word about 825 or 1650 crowns,
             and the amount followed it the moment the producer approved.
             A none costs the producer the whole fee, so nobody claims it
             falsely and there is nothing for an admin to agree to. -->
        <span *ngIf="declarationOpen && declarationPays">
          Nobody has checked this claim yet, and it is already in the amount.
        </span>
        <span *ngIf="declarationOpen && !declarationPays">
          Nothing to confirm — none costs the producer the whole camera fee.
        </span>
      </div>

      <div class="disputed" *ngIf="declarationDisputed">
        <b>Disputed by {{ game.declarations.disputedByName || game.declarations.disputedBy }}.</b>
        <div>{{ game.declarations.disputeNote }}</div>
        <!-- A dispute never moves money by itself, so say what does. -->
        <div class="muted" *ngIf="game.state === 'approved'">
          The approved amount stands — it was agreed. This is the record of the
          disagreement, and the producer has it in the notes below; settle it
          at invoicing.
        </div>
        <div class="muted" *ngIf="game.state !== 'approved'">
          Nothing has been agreed yet, so this one can still be put right:
          take the game back for editing and the producer declares again.
        </div>
      </div>

      <div class="declaration-actions" *ngIf="isAdmin && game.declarations?.declaredAt">
        <!-- Confirming is only ever offered on a claim that pays. Settling a
             dispute is the exception: an admin who objected to a none and was
             talked round has to be able to close it. -->
        <button mat-stroked-button *ngIf="!declarationConfirmed && (declarationPays || declarationDisputed)"
                [disabled]="busy"
                (click)="confirmDeclaration()">
          {{ declarationDisputed ? 'Settled — confirm it' : 'Confirm the declaration' }}
        </button>
        <!-- Disputing works on any declaration, none included: a producer who
             under-claims is owed the news as much as one who over-claims. -->
        <button mat-stroked-button *ngIf="!disputing && !declarationDisputed"
                [disabled]="busy" (click)="disputing = true">
          Dispute it
        </button>
      </div>

      <div class="add-comment" *ngIf="disputing">
        <mat-form-field appearance="outline" class="grow-field">
          <mat-label>What is wrong with the declaration</mat-label>
          <input matInput [(ngModel)]="disputeDraft">
        </mat-form-field>
        <button mat-flat-button color="warn"
                [disabled]="busy || !disputeDraft.trim()"
                (click)="disputeDeclaration()">Dispute</button>
        <button mat-stroked-button [disabled]="busy" (click)="disputing = false">Cancel</button>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Criteria -->
  <mat-card class="card">
    <mat-card-header>
      <mat-card-title>Criteria</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <table class="criteria">
        <thead>
          <tr>
            <th>Criterion</th>
            <th>Verdict</th>
            <th>First seen</th>
            <th>What the tool found</th>
            <th *ngIf="canDecide">Ruling</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of rows">
            <tr [ngClass]="'group-' + row.def.group">
              <td>
                {{ row.def.label }}
                <span class="role" *ngIf="row.def.role === 'gate'" matTooltip="Absolute condition: missing means no payment at all">required</span>
                <span class="role info" *ngIf="row.def.role === 'info'" matTooltip="Measured, but the contract does not pay for it">not paid</span>
                <span class="role" *ngIf="maximumCutPercent(row) !== null"
                      matTooltip="Cuts a share off the production fee; the commentary, the replay and the operated camera are untouched">
                  up to −{{ maximumCutPercent(row) }}%
                </span>
              </td>
              <td>
                <span class="verdict {{ verdictClass(row) }}">{{ verdict(row) }}</span>
                <div class="muted" *ngIf="row.decision">
                  ruled by {{ row.decision.byName || row.decision.by }}<span *ngIf="row.decision.note">: {{ row.decision.note }}</span>
                </div>
              </td>
              <td class="muted">
                <!-- The timestamp is a seek: the reviewer lands on the moment
                     rather than scrubbing for it. -->
                <a *ngIf="row.at !== null && isAdmin; else plainTime"
                   class="seek" (click)="seek(row.at)">{{ time(row.at) }}</a>
                <ng-template #plainTime>{{ time(row.at) }}</ng-template>
              </td>
              <td class="muted">
                {{ row.note }}
              </td>
              <td *ngIf="canDecide" class="actions">
                <!-- The operated camera is the producer's to declare and the
                     admin's to confirm, in the card above; ruling it here would
                     be a second, quieter way to change the same number. -->
                <ng-container *ngIf="editing !== row.def.id && row.def.role !== 'declared'">
                  <button mat-stroked-button (click)="startEditing(row)">Rule</button>
                </ng-container>
                <ng-container *ngIf="editing === row.def.id">
                  <mat-form-field appearance="outline" class="note">
                    <mat-label>Reason</mat-label>
                    <input matInput [(ngModel)]="noteDraft[row.def.id]">
                  </mat-form-field>
                  <!-- Criteria that are a matter of degree are dialled, not
                       answered: the admin sets what it costs the production
                       fee, anywhere from nothing to the contract's maximum. -->
                  <ng-container *ngIf="maximumCutPercent(row) !== null; else plainRuling">
                    <mat-form-field appearance="outline" class="cut">
                      <mat-label>Production cut %</mat-label>
                      <input matInput type="number" min="0" step="5"
                             [max]="maximumCutPercent(row)"
                             [(ngModel)]="cutDraft[row.def.id]">
                      <mat-hint>0 – {{ maximumCutPercent(row) }} %</mat-hint>
                    </mat-form-field>
                    <button mat-flat-button color="primary" [disabled]="busy"
                            (click)="applyCut(row)">Apply cut</button>
                  </ng-container>
                  <ng-template #plainRuling>
                    <button mat-flat-button color="primary" [disabled]="busy" (click)="decide(row, 1)">Met</button>
                    <button mat-stroked-button [disabled]="busy" (click)="decide(row, 0)">Not met</button>
                  </ng-template>
                  <button mat-button [disabled]="busy" (click)="editing = null">Cancel</button>
                </ng-container>
              </td>
            </tr>
            <tr *ngIf="row.def.id === 'liveCommentary' && lineupAudio" class="evidence-row">
              <td [attr.colspan]="canDecide ? 5 : 4">
                <app-evidence-audio [path]="lineupAudio.storagePath"
                                    [caption]="audioCaption">
                </app-evidence-audio>
              </td>
            </tr>
            <tr *ngIf="row.evidence.length" class="evidence-row">
              <td [attr.colspan]="canDecide ? 5 : 4">
                <div class="evidence">
                  <app-evidence-image *ngFor="let event of row.evidence"
                                      [path]="event.storagePath"
                                      [caption]="(event.slot || event.kind) + ' · ' + time(event.start)">
                  </app-evidence-image>
                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </mat-card-content>
  </mat-card>

  <!-- Comments -->
  <mat-card class="card">
    <mat-card-header>
      <mat-card-title>Conversation</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <div class="comment" *ngFor="let comment of comments">
        <div class="comment-head">
          <b>{{ comment.byName || comment.by }}</b>
          <span class="muted"> on {{ criterionLabel(comment.criterion) }}</span>
          <span class="chip resolved" *ngIf="comment.resolvedAt">resolved</span>
          <button mat-button *ngIf="isAdmin && !comment.resolvedAt" [disabled]="busy"
                  (click)="resolveComment(comment)">Mark resolved
          </button>
        </div>
        <div>{{ comment.text }}</div>
      </div>
      <div class="muted" *ngIf="!comments.length">Nothing said yet.</div>
      <div class="add-comment">
        <mat-form-field appearance="outline" class="grow-field">
          <mat-label>Add a note</mat-label>
          <input matInput [(ngModel)]="commentDraft">
        </mat-form-field>
        <button mat-stroked-button [disabled]="busy || !commentDraft.trim()" (click)="addComment()">Post</button>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- What each role may do next -->
  <div class="actions-bar">
    <ng-container *ngIf="isAdmin">
      <button mat-flat-button color="primary" [disabled]="!canRelease || busy" (click)="release()">
        Release to the producer
      </button>
      <span class="muted" *ngIf="canDecide && !canRelease">
        {{ pending.length }} criteri{{ pending.length === 1 ? 'on' : 'a' }} left to rule on.
      </span>

      <!-- Back to the admin's desk while the producer has not approved yet. -->
      <button mat-stroked-button *ngIf="canWithdraw && !withdrawing"
              [disabled]="busy" (click)="withdrawing = true">
        Take back for editing
      </button>
      <ng-container *ngIf="withdrawing">
        <span class="muted">
          It leaves the producer's list until you release it again. Rulings are kept.
        </span>
        <button mat-flat-button color="warn" [disabled]="busy" (click)="withdraw()">
          Take it back
        </button>
        <button mat-stroked-button [disabled]="busy" (click)="withdrawing = false">
          Cancel
        </button>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="canAct">
      <button mat-flat-button color="primary"
              [disabled]="busy || declaration === null"
              (click)="approve()">
        Approve {{ reward.total | number }} {{ reward.currency }}
      </button>
      <button mat-stroked-button [disabled]="busy" (click)="returning = !returning">
        Something is wrong
      </button>
      <span class="muted" *ngIf="declaration === null">
        Say whether the broadcast had an operated camera first.
      </span>
    </ng-container>
  </div>

  <mat-card class="card" *ngIf="returning">
    <mat-card-content>
      <p class="muted">Which criterion is it about, and what is wrong with it?</p>
      <div class="return-form">
        <mat-form-field appearance="outline">
          <mat-label>Criterion</mat-label>
          <select matNativeControl [(ngModel)]="returnCriterion">
            <option [ngValue]="null">the broadcast as a whole</option>
            <option *ngFor="let row of rows" [ngValue]="row.def.id">{{ row.def.label }}</option>
          </select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="grow-field">
          <mat-label>What we disagree with</mat-label>
          <input matInput [(ngModel)]="returnDraft">
        </mat-form-field>
        <button mat-flat-button color="warn" [disabled]="busy || !returnDraft.trim()" (click)="returnGame()">
          Return for another look
        </button>
      </div>
    </mat-card-content>
  </mat-card>

</ng-container>

<ng-template #loading>
  <div *ngIf="missing; else stillLoading">
    <button mat-button (click)="back()">
      <mat-icon>arrow_back</mat-icon>
      Back
    </button>
    <p class="muted">
      This broadcast is not in season {{ season?.season }}. Every season is
      kept on its own, so a link from another one only opens once that season
      is picked in the sidebar.
    </p>
  </div>
  <ng-template #stillLoading>
    <p class="muted">Loading…</p>
  </ng-template>
</ng-template>
